import { createContext, useContext, useEffect, useState } from "react";

const LanguageContext = createContext(null);

export const LANGUAGES = ["it", "en"];

export function LanguageProvider({ children }) {
  const [lang, setLang] = useState(() => localStorage.getItem("casa-paola-lang") || "it");

  useEffect(() => {
    localStorage.setItem("casa-paola-lang", lang);
    document.documentElement.lang = lang;
    document.title = translations[lang].docTitle;
  }, [lang]);

  return (
    <LanguageContext.Provider value={{ lang, setLang, t: translations[lang] }}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useLanguage() {
  return useContext(LanguageContext);
}

const translations = {
  it: {
    docTitle: "Casa Paola Arcore · Appartamento mansardato vicino Monza e F1",
    nav: {
      appartamento: "L'Appartamento",
      comfort: "Comfort",
      galleria: "Galleria",
      recensioni: "Recensioni",
      distanze: "Distanze",
      contatti: "Contatti",
    },
    book: "Prenota",
    floating: "Prenota su WhatsApp",
    aria: { openMenu: "Apri menu", closeMenu: "Chiudi menu", backToTop: "Torna all'inizio", language: "Lingua" },
    hero: {
      overline: "Appartamento mansardato · Arcore · Monza e Brianza",
      line1: "Casa Paola,",
      line2: "ad Arcore.",
      subtitle:
        "Un grazioso appartamento mansardato di due vani e servizio al terzo piano: aria condizionata, riscaldamento autonomo e tutto il comfort di casa. A 5 minuti a piedi dalla stazione, a due passi da Milano, dal Parco di Monza e dal circuito di Formula 1.",
      whatsapp: "Prenota su WhatsApp",
      discover: "Scopri l'appartamento",
      stats: [
        ["500 m", "Stazione FS a piedi"],
        ["3,5 km", "Parco di Monza"],
        ["Max 3", "Ospiti"],
      ],
      stationBadge: "5 min dalla stazione",
      guestsBadge: "Max 3 ospiti",
    },
    marquee: [
      "Aria condizionata",
      "Riscaldamento autonomo",
      "Parcheggio gratuito",
      "5 minuti dalla stazione",
      "Vicino al circuito F1",
      "Parco di Monza",
      "Max 3 ospiti",
      "Due passi da Milano",
    ],
    apartment: {
      overline: "L'appartamento",
      title: "Due vani e servizio, sotto le travi del tetto.",
      body: "Un appartamento mansardato accogliente a pochi minuti dal centro del paese: dotato di aria condizionata e riscaldamento autonomo, è pensato per chi cerca un soggiorno tranquillo a due passi da Milano, dal verde del Parco di Monza e dalla velocità dell'autodromo.",
      specs: [
        { title: "Max 3 ospiti", text: "Ideale per coppie, famiglie piccole e trasferte di lavoro." },
        { title: "Zona notte", text: "Piccola zona notte riservata con letto matrimoniale alla francese." },
        { title: "Zona giorno", text: "Letto singolo e TV satellitare." },
        { title: "Servizio", text: "Bagno completo, con tutto l'essenziale per il soggiorno." },
        { title: "Terzo piano", text: "Senza ascensore, ripagato da una vista aperta sui tetti." },
        { title: "Parcheggio gratuito", text: "Fuori dalla proprietà e nelle immediate vicinanze." },
      ],
    },
    comfort: {
      overline: "Comfort",
      titleA: "Tutti i comfort di casa,",
      titleB: "in ogni stagione.",
      chips: ["Aria condizionata", "Riscaldamento autonomo", "TV satellitare", "Parcheggio gratuito", "Stazione a 500 m"],
    },
    gallery: {
      overline: "Galleria",
      titleA: "Uno sguardo dentro",
      titleEm: "e fuori",
      titleB: "Casa Paola.",
      hint: "Tocca un'immagine per ingrandirla",
      captions: [
        "Soggiorno · letto singolo",
        "L'esterno di Casa Paola",
        "Zona pranzo",
        "Cucina open space",
        "Bagno · vasca e velux",
        "Camera · letto alla francese",
        "Bagno · zona lavabo",
      ],
      ctaTitle: "Ti piace? Prenota la tua pausa.",
      ctaSub: "Risposta rapida su WhatsApp",
      enlarge: "Ingrandisci",
      close: "Chiudi",
    },
    reviews: {
      overline: "Recensioni",
      titleA: "Le parole di chi è",
      titleEm: "già stato",
      titleB: "da noi.",
      cta: "Raccontaci il tuo soggiorno",
      ratingLabel: "Valutazione 5 su 5",
      details: ["Agosto 2026", "Giugno 2026", "Febbraio 2026"],
    },
    distances: {
      overline: "Distanze & luoghi",
      titleA: "Tutto vicino.",
      titleEm: "Milano compresa.",
      mapsCta: "Apri in Google Maps",
      tabs: { tutti: "Tutti", spostamenti: "Spostamenti", "natura-sport": "Natura & Sport", sanita: "Sanità" },
      items: [
        { name: "Stazione FS dei treni", distance: "500 m · 5 min a piedi", tab: "spostamenti", icon: "train" },
        { name: "Ingresso Parco di Monza", distance: "3,5 km", tab: "natura-sport", icon: "trees" },
        { name: "Circuito di Formula 1", distance: "A pochi minuti", tab: "natura-sport", icon: "trophy" },
        { name: "Ospedale di Vimercate", distance: "5 km", tab: "sanita", icon: "health" },
        { name: "Golf Club Usmate", distance: "6 km", tab: "natura-sport", icon: "flag" },
        { name: "U-Stadium & Candy Arena", distance: "7 km", tab: "natura-sport", icon: "landmark" },
        { name: "Policlinico di Monza", distance: "8 km", tab: "sanita", icon: "health" },
        { name: "Ospedale San Gerardo Monza", distance: "9 km", tab: "sanita", icon: "health" },
        { name: "Aeroporto Milano Linate", distance: "18 km", tab: "spostamenti", icon: "plane" },
        { name: "Aeroporto Orio al Serio (BG)", distance: "40 km", tab: "spostamenti", icon: "plane" },
      ],
      map: {
        overline: "Dove siamo",
        title: "Via Baracca 15, Arcore",
        body: "A pochi minuti dal centro del paese e a 5 minuti a piedi dalla stazione ferroviaria: il punto di partenza ideale tra Milano, Monza e la Brianza.",
      },
    },
    booking: {
      overline: "Prenotazioni",
      titleA: "Pronto a scoprire Monza",
      titleEm: "dall'alto dei tetti?",
      body: "Scrivici su WhatsApp per disponibilità, prezzi e qualsiasi domanda sul soggiorno. Rispondiamo in giornata.",
      whatsapp: "Scrivici su WhatsApp",
    },
    footer: {
      tagline:
        "Appartamento mansardato ad Arcore, a due passi dal Parco di Monza, dal circuito di Formula 1 e dalla stazione ferroviaria. Monza e Brianza, Lombardia.",
      explore: "Esplora",
      contacts: "Contatti",
      rights: "© 2026 Casa Paola Arcore",
      specs: "Max 3 ospiti · Terzo piano senza ascensore",
    },
  },
  en: {
    docTitle: "Casa Paola Arcore · Attic apartment near Monza and F1",
    nav: {
      appartamento: "The Apartment",
      comfort: "Amenities",
      galleria: "Gallery",
      recensioni: "Reviews",
      distanze: "Location",
      contatti: "Contact",
    },
    book: "Book now",
    floating: "Book on WhatsApp",
    aria: { openMenu: "Open menu", closeMenu: "Close menu", backToTop: "Back to top", language: "Language" },
    hero: {
      overline: "Attic apartment · Arcore · Monza & Brianza",
      line1: "Casa Paola,",
      line2: "in Arcore.",
      subtitle:
        "A charming attic apartment with two rooms and a bathroom on the third floor: air conditioning, independent heating and all the comforts of home. A 5-minute walk from the station, a stone's throw from Milan, Monza Park and the Formula 1 circuit.",
      whatsapp: "Book on WhatsApp",
      discover: "Discover the apartment",
      stats: [
        ["500 m", "Train station on foot"],
        ["3.5 km", "Monza Park"],
        ["Max 3", "Guests"],
      ],
      stationBadge: "5 min from the station",
      guestsBadge: "Max 3 guests",
    },
    marquee: [
      "Air conditioning",
      "Independent heating",
      "Free parking",
      "5 minutes from the station",
      "Near the F1 circuit",
      "Monza Park",
      "Max 3 guests",
      "A stone's throw from Milan",
    ],
    apartment: {
      overline: "The apartment",
      title: "Two rooms and a bathroom, under the roof beams.",
      body: "A cosy attic apartment a few minutes from the town centre: with air conditioning and independent heating, it is designed for those seeking a quiet stay a stone's throw from Milan, the greenery of Monza Park and the speed of the racetrack.",
      specs: [
        { title: "Max 3 guests", text: "Ideal for couples, small families and business trips." },
        { title: "Sleeping area", text: "A small reserved sleeping area with a French double bed." },
        { title: "Living area", text: "Single bed and satellite TV." },
        { title: "Bathroom", text: "Full bathroom, with everything you need for your stay." },
        { title: "Third floor", text: "No lift, rewarded by an open view over the rooftops." },
        { title: "Free parking", text: "Outside the property and in the immediate vicinity." },
      ],
    },
    comfort: {
      overline: "Amenities",
      titleA: "All the comforts of home,",
      titleB: "in every season.",
      chips: ["Air conditioning", "Independent heating", "Satellite TV", "Free parking", "Station 500 m away"],
    },
    gallery: {
      overline: "Gallery",
      titleA: "A look inside",
      titleEm: "and outside",
      titleB: "Casa Paola.",
      hint: "Tap an image to enlarge it",
      captions: [
        "Living room · single bed",
        "The exterior of Casa Paola",
        "Dining area",
        "Open-plan kitchen",
        "Bathroom · tub and skylight",
        "Bedroom · French bed",
        "Bathroom · sink area",
      ],
      ctaTitle: "Like it? Book your break.",
      ctaSub: "Quick reply on WhatsApp",
      enlarge: "Enlarge",
      close: "Close",
    },
    reviews: {
      overline: "Reviews",
      titleA: "Words from those who have",
      titleEm: "already stayed",
      titleB: "with us.",
      cta: "Tell us about your stay",
      ratingLabel: "Rated 5 out of 5",
      details: ["August 2026", "June 2026", "February 2026"],
    },
    distances: {
      overline: "Distances & places",
      titleA: "Everything close by.",
      titleEm: "Milan included.",
      mapsCta: "Open in Google Maps",
      tabs: { tutti: "All", spostamenti: "Getting around", "natura-sport": "Nature & Sport", sanita: "Healthcare" },
      items: [
        { name: "Train station", distance: "500 m · 5 min walk", tab: "spostamenti", icon: "train" },
        { name: "Monza Park entrance", distance: "3.5 km", tab: "natura-sport", icon: "trees" },
        { name: "Formula 1 Circuit", distance: "A few minutes away", tab: "natura-sport", icon: "trophy" },
        { name: "Vimercate Hospital", distance: "5 km", tab: "sanita", icon: "health" },
        { name: "Golf Club Usmate", distance: "6 km", tab: "natura-sport", icon: "flag" },
        { name: "U-Stadium & Candy Arena", distance: "7 km", tab: "natura-sport", icon: "landmark" },
        { name: "Policlinico di Monza", distance: "8 km", tab: "sanita", icon: "health" },
        { name: "San Gerardo Hospital Monza", distance: "9 km", tab: "sanita", icon: "health" },
        { name: "Milan Linate Airport", distance: "18 km", tab: "spostamenti", icon: "plane" },
        { name: "Orio al Serio Airport (BG)", distance: "40 km", tab: "spostamenti", icon: "plane" },
      ],
      map: {
        overline: "Where we are",
        title: "Via Baracca 15, Arcore",
        body: "A few minutes from the town centre and a 5-minute walk from the railway station: the ideal starting point between Milan, Monza and Brianza.",
      },
    },
    booking: {
      overline: "Bookings",
      titleA: "Ready to discover Monza",
      titleEm: "from above the rooftops?",
      body: "Message us on WhatsApp for availability, prices and any questions about your stay. We reply within the day.",
      whatsapp: "Message us on WhatsApp",
    },
    footer: {
      tagline:
        "Attic apartment in Arcore, a stone's throw from Monza Park, the Formula 1 circuit and the railway station. Monza and Brianza, Lombardy.",
      explore: "Explore",
      contacts: "Contact",
      rights: "© 2026 Casa Paola Arcore",
      specs: "Max 3 guests · Third floor, no lift",
    },
  },
};
